/**
 * useStreamingArtifact Hook
 *
 * Detecta e extrai artifacts em tempo real durante streaming
 */

import { useState, useCallback } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface StreamingArtifact {
  id: string;
  type: string;
  title: string;
  language?: string;
  content: string;
  isComplete: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export const useStreamingArtifact = () => {
  const [streamingArtifact, setStreamingArtifact] =
    useState<StreamingArtifact | null>(null);

  /**
   * Processa texto em streaming e detecta artifacts
   * Retorna: { cleanText, artifact }
   */
  const processStreamingText = useCallback((fullText: string) => {
    const openTagRegex = /<(\w*[Aa]rtifact)\s+([^>]+)>/;
    const openMatch = fullText.match(openTagRegex);

    if (!openMatch) {
      setStreamingArtifact(null);
      return {
        cleanText: fullText,
        artifact: null,
      };
    }

    const tagName = openMatch[1];
    const attributes = openMatch[2];
    const openTagEnd = openMatch.index! + openMatch[0].length;

    const closingTagRegex = new RegExp(`</${tagName}>`, 'i');
    const closingMatch = fullText.substring(openTagEnd).match(closingTagRegex);

    let content: string;
    let isComplete: boolean;
    let fullMatch: string;

    if (closingMatch && closingMatch.index !== undefined) {
      isComplete = true;
      const closingTagStart = openTagEnd + closingMatch.index;
      content = fullText.substring(openTagEnd, closingTagStart);
      fullMatch = fullText.substring(
        openMatch.index!,
        closingTagStart + closingMatch[0].length
      );
    } else {
      isComplete = false;
      content = fullText.substring(openTagEnd);
      fullMatch = fullText.substring(openMatch.index!);
    }

    const extractAttr = (attrName: string): string | undefined => {
      const attrRegex = new RegExp(`${attrName}=(['"])(.*?)\\1`);
      const attrMatch = attributes.match(attrRegex);
      return attrMatch ? attrMatch[2] : undefined;
    };

    const id = extractAttr('identifier') || `artifact-${Date.now()}`;
    const type = extractAttr('type') || 'text/markdown';
    const title = extractAttr('title') || 'Artifact';
    const language = extractAttr('language');

    const artifact: StreamingArtifact = {
      id,
      type,
      title,
      language,
      content: content || '',
      isComplete,
    };

    setStreamingArtifact(artifact);

    const beforeArtifact = fullText.substring(0, openMatch.index);

    let cleanText: string;

    if (!isComplete) {
      cleanText = beforeArtifact.trim();
    } else {
      const afterArtifact = fullText.substring(
        openMatch.index! + fullMatch.length
      );
      cleanText = (beforeArtifact + afterArtifact).trim();
    }

    return {
      cleanText,
      artifact,
    };
  }, []);

  /**
   * Limpa o artifact atual
   */
  const clearArtifact = useCallback(() => {
    setStreamingArtifact(null);
  }, []);

  return {
    streamingArtifact,
    processStreamingText,
    clearArtifact,
  };
};

export default useStreamingArtifact;
